import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js'
import { HDRLoader } from 'three/addons/loaders/HDRLoader.js'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js'
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js'
import { STATUS_STYLES, styleForCategory } from '../api/topology'
import { createNodeModel } from './nodeModels'
import { resolveNodeModelCategory } from './modelLibrary'
import {
  createGLTFNodeModel,
  nodeModelVariantIndex,
  preloadGLTFModels
} from './gltfNodeModels'

const clone = (value) => JSON.parse(JSON.stringify(value))
const color = (status) => (STATUS_STYLES[status] || STATUS_STYLES.normal).color

function dispose(object) {
  object.traverse((child) => {
    if (child.geometry && !child.geometry.userData.shared) child.geometry.dispose()
    const materials = Array.isArray(child.material) ? child.material : [child.material]
    materials.filter(Boolean).forEach((material) => {
      const textures = new Set(Object.values(material).filter((value) => value?.isTexture))
      textures.forEach((texture) => {
        if (!texture.userData.shared) texture.dispose()
      })
      material.dispose()
    })
  })
  object.removeFromParent()
}

function labelSprite(text, small = false) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const label = String(text || '')
  ctx.font = '600 24px system-ui, sans-serif'
  canvas.width = Math.min(2048, Math.max(72, Math.ceil(ctx.measureText(label).width + 34)))
  canvas.height = 48
  ctx.font = '600 24px system-ui, sans-serif'
  ctx.beginPath()
  ctx.roundRect(1, 1, canvas.width - 2, canvas.height - 2, 12)
  ctx.fillStyle = small ? 'rgba(30, 41, 59, 0.72)' : 'rgba(15, 23, 42, 0.84)'
  ctx.fill()
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.32)'
  ctx.lineWidth = 2
  ctx.stroke()
  ctx.fillStyle = '#f8fafc'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(label, canvas.width / 2, canvas.height / 2, canvas.width - 20)
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 4
  texture.premultiplyAlpha = true
  texture.generateMipmaps = false
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    alphaTest: 0.02,
    depthTest: false,
    depthWrite: false,
    fog: false,
    toneMapped: false
  })
  const sprite = new THREE.Sprite(material)
  sprite.renderOrder = 20
  sprite.layers.set(1)
  const height = small ? 3.4 : 4.8
  sprite.scale.set(canvas.width / canvas.height * height, height, 1)
  sprite.userData.isTopologyLabel = true
  return sprite
}

function backdropTexture() {
  const canvas = document.createElement('canvas')
  canvas.width = 1536
  canvas.height = 1024
  const context = canvas.getContext('2d')
  const gradient = context.createLinearGradient(0, 0, 0, canvas.height)
  gradient.addColorStop(0, '#050c18')
  gradient.addColorStop(0.46, '#101f35')
  gradient.addColorStop(1, '#070f1d')
  context.fillStyle = gradient
  context.fillRect(0, 0, canvas.width, canvas.height)
  // Static atmospheric texture: no additional animation or draw calls.
  const glow = context.createRadialGradient(820, 420, 20, 820, 420, 820)
  glow.addColorStop(0, 'rgba(38, 116, 154, 0.18)')
  glow.addColorStop(0.55, 'rgba(28, 73, 121, 0.08)')
  glow.addColorStop(1, 'rgba(12, 24, 44, 0)')
  context.fillStyle = glow
  context.fillRect(0, 0, canvas.width, canvas.height)
  const gridFade = context.createLinearGradient(0, 420, 0, 1024)
  gridFade.addColorStop(0, 'rgba(92, 158, 195, 0)')
  gridFade.addColorStop(0.55, 'rgba(92, 158, 195, 0.07)')
  gridFade.addColorStop(1, 'rgba(92, 158, 195, 0.025)')
  context.strokeStyle = gridFade
  context.lineWidth = 1
  context.beginPath()
  for (let x = -1800; x <= 3300; x += 180) {
    context.moveTo(768 + (x - 768) * 0.1, 420)
    context.lineTo(x, 1024)
  }
  for (const y of [450, 480, 520, 573, 641, 727, 834, 964]) {
    context.moveTo(0, y)
    context.lineTo(1536, y)
  }
  context.stroke()
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.magFilter = THREE.LinearFilter
  texture.minFilter = THREE.LinearFilter
  texture.generateMipmaps = false
  return texture
}

function softShadowTexture() {
  const canvas = document.createElement('canvas')
  canvas.width = 128
  canvas.height = 128
  const context = canvas.getContext('2d')
  const gradient = context.createRadialGradient(64, 64, 4, 64, 64, 62)
  gradient.addColorStop(0, 'rgba(255,255,255,0.88)')
  gradient.addColorStop(0.32, 'rgba(255,255,255,0.48)')
  gradient.addColorStop(1, 'rgba(255,255,255,0)')
  context.fillStyle = gradient
  context.fillRect(0, 0, canvas.width, canvas.height)
  const texture = new THREE.CanvasTexture(canvas)
  texture.userData.shared = true
  texture.colorSpace = THREE.SRGBColorSpace
  texture.magFilter = THREE.LinearFilter
  texture.minFilter = THREE.LinearFilter
  texture.generateMipmaps = false
  return texture
}

// Keep the component's editing contract independent of the WebGL scene.
class Item {
  constructor(type, model) { this.type = type; this.model = model; this.selected = false }
  getModel() { return this.model }
  getType() { return this.type }
}

export default class ThreeTopology {
  constructor({ container, onHover }) {
    this.container = container
    this.onHover = onHover
    this.items = []
    this.handlers = {}
    this.mode = 'default'
    this.scene = new THREE.Scene()
    this.backgroundTexture = backdropTexture()
    this.contactShadowTexture = softShadowTexture()
    this.scene.background = this.backgroundTexture
    this.scene.fog = new THREE.Fog('#0c192b', 1050, 2500)
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 20000)
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance'
    })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.toneMapping = THREE.AgXToneMapping
    this.renderer.toneMappingExposure = 1.04
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFShadowMap
    const room = new RoomEnvironment()
    const pmrem = new THREE.PMREMGenerator(this.renderer)
    this.environmentTarget = pmrem.fromScene(room, 0.04)
    this.scene.environment = this.environmentTarget.texture
    this.scene.environmentIntensity = 1.0
    room.dispose()
    const envBase = (process.env.BASE_URL || '/').replace(/\/?$/, '/')
    this.environmentRequest = new HDRLoader()
      .loadAsync(`${envBase}env/studio_small_03_1k.hdr`)
      .then((texture) => {
        if (this.destroyed) {
          texture.dispose()
          pmrem.dispose()
          return
        }
        texture.mapping = THREE.EquirectangularReflectionMapping
        const hdrTarget = pmrem.fromEquirectangular(texture)
        texture.dispose()
        this.environmentTarget?.dispose()
        this.environmentTarget = hdrTarget
        this.scene.environment = hdrTarget.texture
        this.scene.environmentIntensity = 1.12
        pmrem.dispose()
      })
      .catch((error) => {
        pmrem.dispose()
        console.warn('[ThreeTopology] HDR environment failed, keeping RoomEnvironment', error)
      })
    this.renderer.domElement.tabIndex = 0
    this.renderer.domElement.setAttribute('aria-label', '三维网络拓扑：拖动空白处旋转，滚轮缩放，右键平移，拖动节点调整位置')
    container.appendChild(this.renderer.domElement)
    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.enableDamping = true
    this.controls.minDistance = 45
    this.scene.add(new THREE.HemisphereLight(0xf4f7fa, 0x66717c, 0.82))
    this.keyLight = new THREE.DirectionalLight(0xfff6ea, 2.05)
    this.keyLight.position.set(240, 420, 300)
    this.keyLight.castShadow = true
    this.keyLight.shadow.mapSize.set(2048, 2048)
    this.keyLight.shadow.bias = -0.00025
    this.keyLight.shadow.normalBias = 0.2
    this.keyLight.shadow.radius = 2.2
    this.scene.add(this.keyLight, this.keyLight.target)
    const fillLight = new THREE.DirectionalLight(0xd7e8f5, 0.68)
    fillLight.position.set(-320, 140, -220)
    this.scene.add(fillLight)
    const renderTarget = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      samples: 4
    })
    this.composer = new EffectComposer(this.renderer, renderTarget)
    this.renderPass = new RenderPass(this.scene, this.camera)
    this.gtaoPass = new GTAOPass(this.scene, this.camera, 1, 1)
    this.gtaoPass.output = GTAOPass.OUTPUT.Default
    this.gtaoPass.blendIntensity = 0.34
    this.gtaoPass.updateGtaoMaterial({
      radius: 0.5,
      distanceExponent: 1.4,
      thickness: 1.1,
      distanceFallOff: 1.0,
      scale: 1.0,
      samples: 16
    })
    this.outputPass = new OutputPass()
    this.composer.addPass(this.renderPass)
    this.composer.addPass(this.gtaoPass)
    this.composer.addPass(this.outputPass)
    this.usePostProcessing = false
    this.group = new THREE.Group()
    this.scene.add(this.group)
    this.raycaster = new THREE.Raycaster()
    this.raycaster.params.Line.threshold = 5
    this.pointer = new THREE.Vector2()
    this.timer = new THREE.Timer()
    this.timer.connect(document)
    this.listeners = {
      pointerdown: (event) => this.pointerDown(event),
      pointermove: (event) => this.pointerMove(event),
      pointerup: (event) => this.pointerUp(event),
      pointercancel: () => this.cancelDrag(),
      pointerleave: () => this.onHover(null),
      dblclick: (event) => { const item = this.pick(event); if (item?.type === 'node') this.emit('node:dblclick', { item }) }
    }
    Object.entries(this.listeners).forEach(([name, fn]) => this.renderer.domElement.addEventListener(name, fn, name === 'pointerdown'))
    this.resizeObserver = new ResizeObserver(() => this.changeSize(container.clientWidth, container.clientHeight))
    this.resizeObserver.observe(container)
    this.changeSize(container.clientWidth, container.clientHeight)
    this.renderer.setAnimationLoop((timestamp) => {
      this.controls.update()
      this.timer.update(timestamp)
      const pulse = 0.88 + Math.sin(this.timer.getElapsed() * 2.6) * 0.12
      this.items.forEach((item) => {
        if (item.type === 'node' && item.indicator && !item.selected) {
          item.indicator.emissiveIntensity = (item.indicator.userData.baseEmissiveIntensity || 0.9) * pulse
        }
      })
      if (this.usePostProcessing) this.composer.render()
      else this.renderer.render(this.scene, this.camera)
      this.renderLabelOverlay()
    })
  }
  on(name, fn) { this.handlers[name] = fn }
  emit(name, event) { if (this.handlers[name]) this.handlers[name](event) }
  renderLabelOverlay() {
    const autoClear = this.renderer.autoClear
    const background = this.scene.background
    this.renderer.autoClear = false
    this.scene.background = null
    this.camera.layers.set(1)
    this.renderer.render(this.scene, this.camera)
    this.camera.layers.set(0)
    this.scene.background = background
    this.renderer.autoClear = autoClear
  }
  get(name) { return name === 'destroyed' ? this.destroyed : undefined }
  getContainer() { return this.container }
  getNodes() { return this.items.filter((item) => item.type === 'node') }
  getEdges() { return this.items.filter((item) => item.type === 'edge') }
  findById(id) { return this.items.find((item) => item.model.id === id) }
  findAllByState(type) { return this.items.filter((item) => item.type === type && item.selected) }
  setItemState(item, state, selected) { item.selected = selected; this.paintSelection(item) }
  setMode(mode) { this.mode = mode; this.edgeSource = null }
  data(data) {
    this.extra = clone(data)
    const used = new Set((data.nodes || []).map((node) => String(node.id)))
    this.items = (data.nodes || []).map((node) => new Item('node', { ...clone(node), id: String(node.id) }))
    ;(data.edges || []).forEach((edge, index) => {
      let id = edge.id ? String(edge.id) : `edge-${index}`
      while (used.has(id)) id += '-edge'
      used.add(id)
      this.items.push(new Item('edge', { ...clone(edge), id, source: String(edge.source), target: String(edge.target) }))
    })
    this.layout()
  }
  // Deterministic spatial seed + bounded force relaxation in all three axes.
  layout() {
    const nodes = this.items.filter((item) => item.type === 'node')
    const radius = Math.max(95, Math.cbrt(nodes.length) * 65)
    nodes.forEach(({ model: m }, i) => {
      if ([m.x, m.y, m.z].every(Number.isFinite)) return
      const y = 1 - 2 * (i + 0.5) / Math.max(1, nodes.length)
      const angle = i * Math.PI * (3 - Math.sqrt(5))
      m.x = Math.cos(angle) * Math.sqrt(1 - y * y) * radius
      m.y = y * radius
      m.z = Math.sin(angle) * Math.sqrt(1 - y * y) * radius
    })
    const positions = new Map(nodes.map(({ model: m }) => [m.id, new THREE.Vector3(m.x, m.y, m.z)]))
    const edges = this.getEdges()
    if (nodes.length <= 400) for (let step = 0; step < 100; step++) {
      const forces = nodes.map(() => new THREE.Vector3())
      for (let i = 0; i < nodes.length; i++) {
        const p = positions.get(nodes[i].model.id)
        forces[i].addScaledVector(p, -0.008)
        for (let j = i + 1; j < nodes.length; j++) {
          const d = p.clone().sub(positions.get(nodes[j].model.id))
          const length = Math.max(d.length(), 1)
          d.multiplyScalar(1300 / (length * length * length))
          forces[i].add(d); forces[j].sub(d)
        }
      }
      const byId = new Map(nodes.map((node, i) => [node.model.id, forces[i]]))
      edges.forEach(({ model: m }) => {
        const a = positions.get(m.source); const b = positions.get(m.target)
        if (!a || !b) return
        const d = b.clone().sub(a); const length = d.length() || 1
        d.multiplyScalar((length - 125) / length * 0.025)
        byId.get(m.source).add(d); byId.get(m.target).sub(d)
      })
      nodes.forEach((node, i) => positions.get(node.model.id).add(forces[i].clampLength(0, 6)))
    }
    nodes.forEach(({ model: m }) => { const p = positions.get(m.id); Object.assign(m, { x: p.x, y: p.y, z: p.z }) })
  }
  render() { preloadGLTFModels(); this.rebuild(); this.fitView() }
  // glTF 节点模型是异步加载的：就绪后重建一次场景，把占位的程序化模型换成导出的模型。
  queueModelUpgrade() {
    if (this.destroyed || this.modelUpgradeQueued) return
    this.modelUpgradeQueued = true
    setTimeout(() => {
      this.modelUpgradeQueued = false
      if (!this.destroyed) this.rebuild()
    }, 0)
  }
  rebuild() {
    while (this.group.children.length) dispose(this.group.children[0])
    const nodes = this.items.filter((item) => item.type === 'node')
    // Self-shadowing is valuable in close views but doubles draw work. At the
    // scale of a large topology the models occupy only a few pixels, so retain
    // the baked contact shadows and spend the frame budget on interaction.
    this.enableModelShadows = nodes.length <= 32
    this.renderer.shadowMap.enabled = this.enableModelShadows
    this.usePostProcessing = nodes.length <= 32
    this.gtaoPass.enabled = this.usePostProcessing
    nodes.forEach((item) => this.buildNode(item))
    this.getEdges().forEach((item) => this.buildEdge(item))
    this.updateLightingBounds()
  }
  buildNode(item) {
    const m = item.model
    const visual = styleForCategory(m.category)
    // 同类节点复用固定工业设备型号，状态与类别色只影响小面积视觉提示。
    // Blender 导出的 glTF 模型优先；未登记或尚未加载完成时先用程序化模型占位。
    const modelCategory = resolveNodeModelCategory(m)
    const variantIndex = nodeModelVariantIndex(modelCategory, m.id, m.modelId)
    const model = createGLTFNodeModel(modelCategory, m.status, () => this.queueModelUpgrade(), m.id, m.modelId) ||
      createNodeModel(String(m.modelId || '').startsWith('custom-') ? 'generic' : modelCategory, visual, m.status, m.id, variantIndex)
    // Fine PBR maps matter in equipment close-ups, but not when dozens of
    // thumbnail-sized nodes are visible. Drop only those shared detail maps in
    // a dense topology to preserve the interaction budget.
    if (!this.enableModelShadows) {
      model.materials.forEach((material) => {
        for (const key of ['map', 'roughnessMap', 'normalMap']) {
          if (material[key]?.userData.surfaceDetail) material[key] = null
        }
        material.needsUpdate = true
      })
    }
    const group = model.group
    // Halo and label follow the procedural model footprint instead of a fixed size.
    const bounds = new THREE.Box3().setFromObject(group)
    const size = bounds.getSize(new THREE.Vector3())
    // A restrained status ring keeps the model readable instead of wrapping it
    // in the old translucent sphere.
    const haloRadius = Math.max(18, Math.min(34, Math.max(size.x, size.z) * 0.58 + 3))
    const haloOpacity = STATUS_STYLES[m.status] && m.status !== 'normal' ? 0.9 : 0.14
    group.position.set(m.x, m.y, m.z)
    group.traverse((child) => {
      if (!child.isMesh) return
      child.castShadow = this.enableModelShadows
      child.receiveShadow = this.enableModelShadows
      child.userData.item = item
    })
    const contactShadow = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        color: '#172033',
        map: this.contactShadowTexture,
        transparent: true,
        opacity: 0.28,
        depthWrite: false,
        fog: false,
        toneMapped: false
      })
    )
    contactShadow.rotation.x = -Math.PI / 2
    contactShadow.position.y = bounds.min.y - 0.45
    contactShadow.scale.set(Math.max(26, size.x * 1.18), Math.max(20, size.z * 1.18), 1)
    contactShadow.renderOrder = -1
    group.add(contactShadow)
    const halo = new THREE.Mesh(
      new THREE.TorusGeometry(haloRadius, 0.26, 8, 64),
      new THREE.MeshBasicMaterial({
        color: color(m.status),
        transparent: true,
        opacity: haloOpacity,
        depthWrite: false,
        toneMapped: false
      })
    )
    halo.rotation.x = Math.PI / 2
    halo.position.y = bounds.min.y - 0.15
    halo.renderOrder = 1
    group.add(halo)
    const label = labelSprite(m.label || m.id)
    label.position.y = bounds.max.y + 6
    group.add(label)
    item.object = group; item.mesh = model.primary; item.materials = model.materials; item.indicator = model.indicator; item.halo = halo; item.label = label; item.haloOpacity = haloOpacity; item.modelBounds = bounds.clone()
    this.group.add(group)
    this.paintSelection(item)
  }
  buildEdge(item) {
    const m = item.model
    const source = this.findById(m.source); const target = this.findById(m.target)
    if (!source?.object || !target?.object) { item.object = null; return }
    const a = source.object.position; const b = target.object.position
    const group = new THREE.Group()
    const curve = m.source === m.target
      ? new THREE.CatmullRomCurve3([a.clone(), a.clone().add(new THREE.Vector3(45, 30, 0)), a.clone().add(new THREE.Vector3(0, 55, 30)), a.clone()])
      : new THREE.LineCurve3(a.clone(), b.clone())
    const radius = Math.max(0.42, Math.min(1.65, (Number(m.value) || 180) / 650))
    const segments = m.linkType === 'wireless' ? 20 : 1
    for (let i = 0; i < segments; i++) {
      if (segments > 1 && i % 2) continue
      const path = segments === 1 ? curve : new THREE.LineCurve3(curve.getPoint(i / segments), curve.getPoint((i + 1) / segments))
      const statusTone = new THREE.Color(color(m.status))
      const cableTone = statusTone.clone().lerp(new THREE.Color(m.linkType === 'wireless' ? 0x8baac4 : 0x7299b8), 0.48)
      const mesh = new THREE.Mesh(
        new THREE.TubeGeometry(path, m.source === m.target ? 40 : 1, radius, 8, false),
        new THREE.MeshStandardMaterial({
          color: cableTone,
          emissive: statusTone,
          emissiveIntensity: 0.16,
          roughness: 0.64,
          metalness: 0.12,
          envMapIntensity: 0.72
        })
      )
      mesh.userData.item = item
      mesh.userData.baseColor = cableTone.getHex()
      group.add(mesh)
    }
    [['sourceNic', 0.19], ['targetNic', 0.81]].forEach(([key, t]) => {
      if (!m[key]) return
      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(2.1, 16, 10),
        new THREE.MeshStandardMaterial({
          color: '#334155',
          emissive: color(m[key].status),
          emissiveIntensity: 0.34,
          roughness: 0.5,
          metalness: 0.2
        })
      )
      marker.position.copy(curve.getPoint(t)); marker.userData.item = item
      marker.userData.nic = true
      group.add(marker)
    })
    if (m.label || m.linkId) {
      const label = labelSprite(m.label || m.linkId, true)
      label.position.copy(curve.getPoint(0.5)).add(new THREE.Vector3(0, 8, 0))
      group.add(label)
    }
    item.object = group
    this.group.add(group)
    this.paintSelection(item)
  }
  paintSelection(item) {
    if (!item.object) return
    if (item.type === 'node') {
      const materials = item.materials || (item.mesh ? [item.mesh.material] : [])
      materials.forEach((material) => {
        if (!material.emissive) return
        const baseEmissive = material.userData.baseEmissive ?? 0x000000
        const baseIntensity = material.userData.baseEmissiveIntensity ?? 1
        material.emissive.setHex(baseEmissive)
        material.emissiveIntensity = item.selected && material.userData.isIndicator
          ? Math.min(1.6, baseIntensity + 0.35)
          : baseIntensity
      })
      const abnormal = STATUS_STYLES[item.model.status] && item.model.status !== 'normal'
      item.halo.material.color.set(item.selected && !abnormal ? '#fa8c16' : color(item.model.status))
      item.halo.material.opacity = item.selected ? Math.max(0.3, (item.haloOpacity ?? 0.16) + 0.19) : (item.haloOpacity ?? 0.16)
    } else item.object.children.forEach((child) => {
      if (child.isMesh && !child.userData.nic) child.material.color.set(item.selected ? '#fa8c16' : child.userData.baseColor)
    })
  }
  updateLightingBounds() {
    const box = new THREE.Box3().setFromObject(this.group)
    if (box.isEmpty()) return
    const center = box.getCenter(new THREE.Vector3())
    const size = box.getSize(new THREE.Vector3())
    const extent = THREE.MathUtils.clamp(Math.max(size.x, size.y, size.z) * 0.62 + 100, 360, 1800)
    this.keyLight.target.position.copy(center)
    this.keyLight.position.copy(center).add(new THREE.Vector3(240, 420, 300))
    const shadowCamera = this.keyLight.shadow.camera
    shadowCamera.left = -extent
    shadowCamera.right = extent
    shadowCamera.top = extent
    shadowCamera.bottom = -extent
    shadowCamera.near = 1
    shadowCamera.far = Math.max(1400, extent * 3)
    shadowCamera.updateProjectionMatrix()
    this.keyLight.shadow.needsUpdate = true
  }
  // Evolution changes status only: keep model objects, positions and camera intact.
  updateNodeStatus(item, status) {
    item.model.status = status
    const tone = color(status)
    ;(item.materials || []).forEach((material) => {
      if (material !== item.indicator && !material.userData.isIndicator) return
      material.color?.set(tone)
      if (material.emissive) {
        material.emissive.set(tone)
        material.userData.baseEmissive = material.emissive.getHex()
      }
    })
    item.haloOpacity = STATUS_STYLES[status] && status !== 'normal' ? 0.9 : 0.14
    this.paintSelection(item)
  }
  updateItem(item, patch) {
    Object.assign(item.model, patch)
    if (item.object) dispose(item.object)
    if (item.type === 'node') {
      this.buildNode(item)
      this.refreshEdges(item.model.id)
    } else this.buildEdge(item)
  }
  refreshEdges(nodeId) {
    this.getEdges().filter(({ model }) => [model.source, model.target].includes(nodeId)).forEach((edge) => {
      if (edge.object) dispose(edge.object)
      this.buildEdge(edge)
    })
  }
  addItem(type, model) { const item = new Item(type, clone(model)); this.items.push(item); this.rebuild(); return item }
  removeItem(item) {
    this.items = this.items.filter((candidate) => candidate !== item && !(item.type === 'node' && candidate.type === 'edge' && [candidate.model.source, candidate.model.target].includes(item.model.id)))
    this.rebuild()
    this.emit('canvas:click', {})
  }
  save() { return { ...clone(this.extra), nodes: this.items.filter((item) => item.type === 'node').map((item) => clone(item.model)), edges: this.getEdges().map((item) => clone(item.model)) } }
  changeSize(width, height) {
    width = Math.max(1, width); height = Math.max(1, height)
    this.renderer.setSize(width, height)
    this.composer.setSize(width, height)
    this.gtaoPass.setSize(width, height)
    this.camera.aspect = width / height; this.camera.updateProjectionMatrix()
  }
  fitView() {
    this.setDetailMode(null)
    const box = new THREE.Box3().setFromObject(this.group)
    if (box.isEmpty()) box.setFromCenterAndSize(new THREE.Vector3(), new THREE.Vector3(100, 100, 100))
    const center = box.getCenter(new THREE.Vector3())
    const radius = Math.max(65, box.getSize(new THREE.Vector3()).length() / 2)
    const halfFov = Math.atan(Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2)) * Math.min(1, this.camera.aspect))
    const distance = radius / Math.sin(halfFov) * 1.15
    this.camera.far = Math.max(20000, distance * 10); this.camera.updateProjectionMatrix()
    this.controls.maxDistance = distance * 5
    this.camera.position.copy(center).add(new THREE.Vector3(0.7, 0.45, 1).normalize().multiplyScalar(distance))
    this.controls.target.copy(center); this.controls.update()
  }
  focusItem(item, close = false) {
    if (!item.object) return
    this.setDetailMode(close && item.type === 'node' ? item : null)
    const box = close && item.modelBounds
      ? item.modelBounds.clone().translate(item.object.position)
      : new THREE.Box3().setFromObject(item.object)
    const center = box.getCenter(new THREE.Vector3())
    const offset = this.camera.position.clone().sub(this.controls.target)
    if (close) {
      const radius = Math.max(24, box.getSize(new THREE.Vector3()).length() / 2)
      const halfFov = Math.atan(Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2)) * Math.min(1, this.camera.aspect))
      const distance = radius / Math.sin(halfFov) * 1.15
      const direction = offset.lengthSq() > 0.001
        ? offset.normalize()
        : new THREE.Vector3(0.7, 0.45, 1).normalize()
      this.camera.position.copy(center).add(direction.multiplyScalar(distance))
      this.controls.maxDistance = Math.max(this.controls.maxDistance, distance * 5)
    } else this.camera.position.copy(center).add(offset)
    this.controls.target.copy(center); this.controls.update()
  }
  setDetailMode(nodeItem) {
    const enabled = Boolean(nodeItem)
    this.items.forEach((item) => {
      if (item.type === 'node') {
        if (item.object) item.object.visible = !enabled || item === nodeItem
        if (item.label) item.label.visible = !enabled
        if (item.halo) item.halo.visible = !enabled
      } else if (item.object) item.object.visible = !enabled
    })
  }
  getPointByClient() { const p = this.controls.target; return { x: p.x, y: p.y, z: p.z } }
  pick(event) {
    const rect = this.renderer.domElement.getBoundingClientRect()
    this.pointer.set((event.clientX - rect.left) / rect.width * 2 - 1, -(event.clientY - rect.top) / rect.height * 2 + 1)
    this.scene.updateMatrixWorld(true)
    this.raycaster.setFromCamera(this.pointer, this.camera)
    return this.raycaster.intersectObjects(this.group.children, true).find((hit) => hit.object.userData.item)?.object.userData.item || null
  }
  pointerDown(event) {
    if (event.button !== 0) return
    const item = this.pick(event)
    this.down = { x: event.clientX, y: event.clientY, item }
    if (item?.type === 'node') {
      this.controls.enabled = false
      this.renderer.domElement.setPointerCapture(event.pointerId)
      this.dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(this.camera.getWorldDirection(new THREE.Vector3()), item.object.position)
      this.dragOffset = new THREE.Vector3()
      const hit = this.raycaster.ray.intersectPlane(this.dragPlane, new THREE.Vector3())
      if (hit) this.dragOffset.copy(item.object.position).sub(hit)
    }
  }
  pointerMove(event) {
    const item = this.pick(event)
    if (this.down?.item?.type === 'node' && this.mode === 'default' && Math.hypot(event.clientX - this.down.x, event.clientY - this.down.y) > 4) {
      const point = this.raycaster.ray.intersectPlane(this.dragPlane, new THREE.Vector3())
      if (point) { point.add(this.dragOffset); Object.assign(this.down.item.model, { x: point.x, y: point.y, z: point.z }); this.down.item.object.position.copy(point); this.refreshEdges(this.down.item.model.id) }
      this.onHover(null)
      return
    }
    this.renderer.domElement.style.cursor = item ? 'pointer' : 'grab'
    this.onHover(item ? { model: item.model, itemType: item.type, x: event.offsetX, y: event.offsetY } : null)
  }
  pointerUp(event) {
    const down = this.down
    const item = this.pick(event)
    this.cancelDrag()
    if (!down) return
    if (this.mode === 'addEdge' && item?.type === 'node') {
      const source = this.edgeSource || (down.item !== item ? down.item : null)
      if (source?.type === 'node' && source !== item) {
        const edge = this.addItem('edge', { id: this.newId('edge'), source: source.model.id, target: item.model.id, status: 'normal', linkType: 'wired', meta: {} })
        this.edgeSource = null; this.emit('aftercreateedge', { edge })
      } else { this.edgeSource = item; this.emit('node:click', { item }) }
      return
    }
    if (Math.hypot(event.clientX - down.x, event.clientY - down.y) > 4) return
    this.items.forEach((candidate) => this.setItemState(candidate, 'selected', candidate === item))
    this.emit(item ? `${item.type}:click` : 'canvas:click', { item })
  }
  cancelDrag() { this.down = null; this.controls.enabled = true }
  newId(prefix) { let id; do { id = `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` } while (this.findById(id)); return id }
  downloadFullImage(name) {
    this.renderer.render(this.scene, this.camera)
    const anchor = document.createElement('a')
    anchor.download = `${name}-3d.png`; anchor.href = this.renderer.domElement.toDataURL('image/png'); anchor.click()
  }
  destroy() {
    this.destroyed = true
    this.resizeObserver.disconnect()
    this.renderer.setAnimationLoop(null)
    this.timer.dispose()
    this.controls.dispose()
    Object.entries(this.listeners).forEach(([name, fn]) => this.renderer.domElement.removeEventListener(name, fn, name === 'pointerdown'))
    dispose(this.scene)
    this.gtaoPass.dispose()
    this.outputPass.dispose()
    this.composer.dispose()
    this.backgroundTexture.dispose()
    this.contactShadowTexture.dispose()
    this.environmentTarget.dispose()
    this.renderer.dispose(); this.renderer.forceContextLoss(); this.renderer.domElement.remove()
  }
}
